import React, { useState, useEffect } from 'react';
import { 
  Modal, 
  Box, 
  Stepper, 
  Step, 
  StepLabel, 
  Button, 
  Typography,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  TooltipProps,
  IconButton,
  styled,
  LinearProgress,
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { Form990Question } from '../../../../types/Form990Types';
import { 
  form990Questions, 
  getInitialResponses,
  getRequiredSchedules 
} from '../../../../data/form990Questions';
import { form990API } from '../../../../services/api';
import { useAuth } from '../../../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Form990PDFHandler } from '../../../../services/Form990PDFHandler';

interface LargeTooltipProps extends Omit<TooltipProps, 'classes'> {
  className?: string;
  classes?: {
    popper?: string;
  };
}

// Update the styled tooltip with proper typing
const LargeTooltip = styled(({ className, ...props }: LargeTooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  '& .MuiTooltip-tooltip': {
    backgroundColor: '#1A2635',
    color: '#FFFFFF',
    maxWidth: 500,
    fontSize: '1rem',
    padding: '24px',
    borderRadius: '8px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    border: '1px solid rgba(99, 179, 237, 0.3)',
    lineHeight: '1.6',
    '& ul': {
      paddingLeft: '24px',
      marginTop: '8px',
      marginBottom: '8px',
      listStyleType: 'none',
    },
    '& .section-header': {
      fontSize: '1rem',
      marginTop: '16px',
      marginBottom: '8px',
      borderBottom: '1px solid currentColor',
      paddingBottom: '4px',
    },
    '& .include-section, & .allowed-section, & .required-section': {
      '& .section-header': {
        color: '#34d399',
        textShadow: '0 0 10px rgba(52, 211, 153, 0.3)',
      },
      '& li': {
        marginBottom: '8px',
        paddingLeft: '8px',
        position: 'relative',
        color: '#34d399',
        textShadow: '0 0 10px rgba(52, 211, 153, 0.3)',
        '&::before': {
          content: '"•"',
          position: 'absolute',
          left: '-16px',
          color: '#34d399',
        }
      }
    },
    '& .yes-section': {
      '& .section-header': {
        color: '#34d399',
        textShadow: '0 0 10px rgba(52, 211, 153, 0.3)',
      },
      '& li': {
        marginBottom: '8px',
        paddingLeft: '8px',
        position: 'relative',
        color: '#34d399',
        textShadow: '0 0 10px rgba(52, 211, 153, 0.3)',
        '&::before': {
          content: '"•"',
          position: 'absolute',
          left: '-16px',
          color: '#34d399',
        }
      }
    },
    '& .examples-section, & .reportable-section, & .need-this-section': {
      '& .section-header': {
        color: '#63B3ED',
      },
      '& li': {
        marginBottom: '8px',
        paddingLeft: '8px',
        position: 'relative',
        color: '#63B3ED',
        '&::before': {
          content: '"•"',
          position: 'absolute',
          left: '-16px',
          color: '#63B3ED',
        }
      }
    },
    '& .exclude-section, & .prohibited-section, & .no-section': {
      '& .section-header': {
        color: '#ef4444',
        textShadow: '0 0 10px rgba(239, 68, 68, 0.3)',
      },
      '& li': {
        marginBottom: '8px',
        paddingLeft: '8px',
        position: 'relative',
        color: '#ef4444',
        textShadow: '0 0 10px rgba(239, 68, 68, 0.3)',
        '&::before': {
          content: '"•"',
          position: 'absolute',
          left: '-16px',
          color: '#ef4444',
        }
      }
    },
    '& .requirements-section': {
      '& .section-header': {
        color: '#34d399',
        textShadow: '0 0 10px rgba(52, 211, 153, 0.3)',
      },
      '& li': {
        marginBottom: '8px',
        paddingLeft: '8px',
        position: 'relative',
        color: '#34d399',
        textShadow: '0 0 10px rgba(52, 211, 153, 0.3)',
        '&::before': {
          content: '"•"',
          position: 'absolute',
          left: '-16px',
          color: '#34d399',
        }
      }
    },
    '& .program-section': {
      '& li': {
        marginBottom: '8px',
        paddingLeft: '8px',
        position: 'relative',
        color: '#FFFFFF',
        '&::before': {
          content: '"•"',
          position: 'absolute',
          left: '-16px',
          color: '#FFFFFF',
        }
      }
    }
  }
});

// Styled components
const StyledModal = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80%',
  maxWidth: 800,
  maxHeight: '90vh',
  overflow: 'auto',
  backgroundColor: '#1E2A3B',
  border: '1px solid rgba(79, 209, 197, 0.2)',
  borderRadius: '12px',
  padding: 0, // Remove padding to accommodate gradient border
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
  '&:focus': {
    outline: 'none',
  },
}));

const GradientBorder = styled(Box)({
  width: '100%',
  height: '4px',
  background: 'linear-gradient(90deg, #4FD1C5 0%, #63B3ED 100%)',
  borderTopLeftRadius: '12px',
  borderTopRightRadius: '12px',
});

const ProgressTracker = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 32px',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
});

const StyledProgress = styled(LinearProgress)({
  height: 4,
  backgroundColor: 'rgba(99, 179, 237, 0.2)',
  '& .MuiLinearProgress-bar': {
    backgroundColor: '#63B3ED',
  },
  borderRadius: 2,
});

const BlueButton = styled(Button)({
  backgroundColor: '#63B3ED',
  '&:hover': {
    backgroundColor: '#4299E1',
  },
  '&:disabled': {
    backgroundColor: 'rgba(255, 255, 255, 0.12)',
  },
});

// Update existing styled components
const StyledStepper = styled(Stepper)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  '& .MuiStepLabel-root .Mui-completed': {
    color: '#63B3ED', // Changed to blue
  },
  '& .MuiStepLabel-root .Mui-active': {
    color: '#63B3ED', // Changed to blue
  },
  '& .MuiStepLabel-label': {
    color: '#FFFFFF',
  },
  '& .MuiStepConnector-line': {
    borderColor: 'rgba(99, 179, 237, 0.2)',
  },
}));

const StyledRadioGroup = styled(RadioGroup)({
  '& .MuiFormControlLabel-root': {
    marginBottom: '12px',
  },
  '& .MuiRadio-root': {
    color: '#4FD1C5',
    '&.Mui-checked': {
      color: '#4FD1C5',
    },
  },
  '& .MuiFormControlLabel-label': {
    color: '#FFFFFF',
  },
});

export interface PartIVQuestionnaireProps {
  responses: Record<string, any>;
  onResponseChange: (newResponses: Record<string, any>) => void;
  autoPopulatedFields: string[];
}

// Update the tooltip content formatting
const formatTooltipContent = (tip: string) => {
  let currentSection = '';
  
  return (
    <div style={{ whiteSpace: 'pre-wrap' }}>
      {tip.split('\n').map((line, index) => {
        if (line.trim().startsWith('A board member is independent if')) {
          currentSection = 'requirements';
          return (
            <div className="requirements-section">
              <div key={index} className="section-header">
                {line}
              </div>
            </div>
          );
        }
        if (line.trim().startsWith('For each program')) {
          currentSection = 'program';
          return (
            <div className="program-section">
              <div key={index}>
                {line}
              </div>
            </div>
          );
        }
        if (line.trim().startsWith('Include:') || line.trim().startsWith('Required') || line.trim().startsWith('Allowed Activities:')) {
          currentSection = 'include';
          return (
            <div className="include-section">
              <div key={index} className="section-header">
                {line}
              </div>
            </div>
          );
        }
        if (line.trim().startsWith('Don\'t Include:') || line.trim().startsWith('Prohibited Activities:') || line.trim().startsWith('Select "No" if:')) {
          currentSection = 'exclude';
          return (
            <div className="exclude-section">
              <div key={index} className="section-header">
                {line}
              </div>
            </div>
          );
        }
        if (line.trim().startsWith('Select "Yes" if')) {
          currentSection = 'yes';
          return (
            <div className="yes-section">
              <div key={index} className="section-header">
                {line}
              </div>
            </div>
          );
        }
        if (line.trim().startsWith('Example') || line.trim().startsWith('Reportable Activities:') || line.trim().startsWith('You need this if:')) {
          currentSection = 'examples';
          return (
            <div className="examples-section">
              <div key={index} className="section-header">
                {line}
              </div>
            </div>
          );
        }
        if (line.trim().includes('You need this if you:')) {
          currentSection = 'need-this';
          return (
            <div className="need-this-section">
              <div key={index} className="section-header">
                {line}
              </div>
            </div>
          );
        }
        if (line.trim().startsWith('Example:')) {
          currentSection = 'examples';
          return (
            <div className="examples-section">
              <div key={index}>
                {line}
              </div>
            </div>
          );
        }
        if (line.trim().startsWith('•')) {
          return (
            <ul key={`${index}-list`} className={`${currentSection}-section`}>
              <li key={index}>
                {line.trim().substring(1).trim()}
              </li>
            </ul>
          );
        }
        if (line.trim().startsWith('Backup withholding')) {
          currentSection = 'include';
          return (
            <div className="include-section">
              <div key={index} className="section-header">
                {line}
              </div>
            </div>
          );
        }
        if (line.trim() === 'Did not provide a valid Tax ID (EIN or SSN)' ||
            line.trim() === 'Received a notice from the IRS requiring backup withholding') {
          return (
            <ul key={`${index}-list`} className={`${currentSection}-section`}>
              <li key={index}>
                {line.trim()}
              </li>
            </ul>
          );
        }
        return (
          <div key={index} style={{ marginBottom: line.trim() ? '8px' : '4px' }}>
            {line}
          </div>
        );
      })}
    </div>
  );
};

// At the top of the component, add a debug helper
const debugLog = (section: string, data: any) => {
  console.group(`🔍 Debug: ${section}`);
  console.log('Data:', data);
  if (data?.data) console.log('Data.data:', data.data);
  if (data?.responses) console.log('Data.responses:', data.responses);
  console.groupEnd();
};

// Update the mapping logic to handle all question types
const mapResponsesFromAPI = (data: any) => {
  console.log('🔍 Debug - Raw API Response:', data);
  const mappedResponses: { [key: string]: any } = {};
  
  // Part III mapping
  if (data?.data?.responses?.organizationInfo) {
    console.log('🔍 Debug - Organization Info:', data.data.responses.organizationInfo);
    const orgInfo = data.data.responses.organizationInfo;
    
    // Map boolean and text fields
    mappedResponses['part3_q1'] = orgInfo.significantChanges ?? false;
    mappedResponses['part3_q6'] = orgInfo.newPrograms ?? false;
    mappedResponses['part3_q7'] = orgInfo.discontinuedOperations ?? false;
    mappedResponses['part3_q8'] = orgInfo.activitiesAndPurpose || '';
    
    // Map numeric fields from the same organizationInfo object
    mappedResponses['part3_q2'] = Number(orgInfo.employeesCount) || 0;
    mappedResponses['part3_q3'] = Number(orgInfo.volunteersCount) || 0;
    mappedResponses['part3_q4'] = Number(orgInfo.totalRevenue) || 0;
    mappedResponses['part3_q5'] = Number(orgInfo.totalExpenses) || 0;
  }

  // Map governance responses
  if (data?.data?.responses?.governance) {
    const governance = data.data.responses.governance;
    mappedResponses['part4_q1'] = governance.boardMeetingDocumentation ?? false;
    mappedResponses['part4_q2'] = governance.committeesWithAuthority ?? false;
    mappedResponses['part4_q3'] = governance.significantDiversion ?? false;
    mappedResponses['part4_q4'] = governance.conflictOfInterestPolicy ?? false;
    mappedResponses['part4_q5'] = governance.whistleblowerPolicy ?? false;
    mappedResponses['part4_q6'] = governance.documentRetentionPolicy ?? false;
  }

  // Map compensation responses
  if (data?.data?.responses?.compensation) {
    const compensation = data.data.responses.compensation;
    mappedResponses['part5_q1'] = compensation.compensationProcess ?? false;
    mappedResponses['part5_q2'] = compensation.independentReview ?? false;
    mappedResponses['part5_q3'] = compensation.compensationSources || [];
  }

  // Map financial practices responses
  if (data?.data?.responses?.financialPractices) {
    const financialPractices = data.data.responses.financialPractices;
    mappedResponses['part6_q1'] = financialPractices.auditedFinancials ?? false;
    mappedResponses['part6_q2'] = financialPractices.auditCommittee ?? false;
    mappedResponses['part6_q3'] = financialPractices.federalGrantRequirements ?? false;
  }

  console.log('🔍 Debug - Final Mapped Responses:', mappedResponses);
  return mappedResponses;
};

// Transform responses for server submission
const transformResponses = (responses: any) => {
  console.log('Transforming responses for submission:', responses);
  return {
    organizationInfo: {
      activitiesAndPurpose: responses.part3_q8 || '',
      significantChanges: Boolean(responses.part3_q1),
      employeesCount: responses.part3_q2 !== undefined ? Number(responses.part3_q2) : null,
      volunteersCount: responses.part3_q3 !== undefined ? Number(responses.part3_q3) : null,
      totalRevenue: responses.part3_q4 !== undefined ? Number(responses.part3_q4) : null,
      totalExpenses: responses.part3_q5 !== undefined ? Number(responses.part3_q5) : null,
      newPrograms: Boolean(responses.part3_q6),
      discontinuedOperations: Boolean(responses.part3_q7)
    },
    governance: {
      boardMeetingDocumentation: Boolean(responses.part4_q1),
      committeesWithAuthority: Boolean(responses.part4_q2),
      significantDiversion: Boolean(responses.part4_q3),
      documentRetention: Boolean(responses.part4_q4),
      conflictOfInterestPolicy: Boolean(responses.part5_q1),
      whistleblowerPolicy: Boolean(responses.part5_q2),
      documentRetentionPolicy: Boolean(responses.part5_q3)
    },
    compensation: {
      compensationProcess: Boolean(responses.part6_q1),
      independentReview: Boolean(responses.part6_q2),
      taxableCompensation: Boolean(responses.part6_q3)
    },
    financialPractices: {
      auditedFinancials: Boolean(responses.part7_q1),
      auditCommittee: Boolean(responses.part7_q2),
      federalGrantRequirements: Boolean(responses.part8_q1)
    }
  };
};

// Map server response back to flat structure
const mapServerResponseToFlatStructure = (serverResponse: any) => {
  console.log('Mapping server response:', serverResponse);
  const flatResponses: any = {};
  
  if (serverResponse?.responses) {
    const { organizationInfo, governance, compensation, financialPractices } = serverResponse.responses;
    
    // Part III - Organization Info
    if (organizationInfo) {
      flatResponses.part3_q1 = organizationInfo.significantChanges ?? false;
      flatResponses.part3_q2 = organizationInfo.employeesCount ?? '';
      flatResponses.part3_q3 = organizationInfo.volunteersCount ?? '';
      flatResponses.part3_q4 = organizationInfo.totalRevenue ?? '';
      flatResponses.part3_q5 = organizationInfo.totalExpenses ?? '';
      flatResponses.part3_q6 = organizationInfo.newPrograms ?? false;
      flatResponses.part3_q7 = organizationInfo.discontinuedOperations ?? false;
      flatResponses.part3_q8 = organizationInfo.activitiesAndPurpose || '';
    }

    // Part IV & V - Governance
    if (governance) {
      flatResponses.part4_q1 = governance.boardMeetingDocumentation ?? false;
      flatResponses.part4_q2 = governance.committeesWithAuthority ?? false;
      flatResponses.part4_q3 = governance.significantDiversion ?? false;
      flatResponses.part4_q4 = governance.documentRetention ?? false;
      flatResponses.part5_q1 = governance.conflictOfInterestPolicy ?? false;
      flatResponses.part5_q2 = governance.whistleblowerPolicy ?? false;
      flatResponses.part5_q3 = governance.documentRetentionPolicy ?? false;
    }

    // Part VI - Compensation
    if (compensation) {
      flatResponses.part6_q1 = compensation.compensationProcess ?? false;
      flatResponses.part6_q2 = compensation.independentReview ?? false;
      flatResponses.part6_q3 = compensation.taxableCompensation ?? false;
    }

    // Part VII & VIII - Financial Practices
    if (financialPractices) {
      flatResponses.part7_q1 = financialPractices.auditedFinancials ?? false;
      flatResponses.part7_q2 = financialPractices.auditCommittee ?? false;
      flatResponses.part8_q1 = financialPractices.federalGrantRequirements ?? false;
    }
  }

  console.log('Mapped flat responses:', flatResponses);
  return flatResponses;
};

// Add this function near the top of the file with other utility functions
const calculateProgress = (responses: Record<string, any>): number => {
  let answeredCount = 0;
  
  form990Questions.forEach(question => {
    const response = responses[question.id];
    if (response !== undefined && response !== null) {
      if (Array.isArray(response)) {
        // For array responses (like lists), consider it answered if not empty
        if (response.length > 0) answeredCount++;
      } else if (typeof response === 'string') {
        // For string responses, consider it answered if not empty
        if (response.trim() !== '') answeredCount++;
      } else if (typeof response === 'number') {
        // For numeric responses, consider it answered if not 0 or if explicitly set to 0
        if (response !== undefined) answeredCount++;
      } else {
        // For boolean responses, consider it answered if true or false
        answeredCount++;
      }
    }
  });

  return answeredCount;
};

export const PartIVQuestionnaire: React.FC<PartIVQuestionnaireProps> = ({
  responses,
  onResponseChange,
  autoPopulatedFields
}) => {
  const handleChange = (questionId: string, value: any) => {
    onResponseChange({
      ...responses,
      [questionId]: value
    });
  };

  return (
    <Box>
      {/* Questionnaire form implementation */}
    </Box>
  );
}; 